// zoomNav — altitude changes animate as one camera move, not a page swap.
// Wraps a react-router navigation in the View Transitions API so the old
// altitude crossfades into the new one (and elements sharing a
// view-transition-name — the zoom spine strip — carry across). Degrades to a
// plain navigation when the browser lacks the API or the user prefers reduced
// motion; never let the transition machinery block the navigation itself.

import { flushSync } from 'react-dom'

export function zoomNavigate(navigate, to, opts) {
  const reduced = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
  if (typeof document === 'undefined' || !document.startViewTransition || reduced) {
    navigate(to, opts)
    return
  }
  try {
    const transition = document.startViewTransition(() => {
      // flushSync so the route change is committed inside the transition's
      // snapshot window; lazy-loaded routes settle as a quick crossfade.
      flushSync(() => navigate(to, opts))
    })
    // A skipped/aborted transition (rapid re-zoom, tab hidden mid-zoom)
    // rejects these promises — "Transition was aborted because of invalid
    // state". The update callback still runs, so the navigation itself has
    // committed; swallow the rejections so they never surface as unhandled.
    transition.ready?.catch(() => {})
    transition.finished?.catch(() => {})
    transition.updateCallbackDone?.catch(() => {})
  } catch {
    navigate(to, opts)
  }
}

// For <Link>-style anchors: intercept only a plain left-click (no modifier
// keys), preserving open-in-new-tab and middle-click semantics.
export function zoomLinkHandler(navigate, to) {
  return (e) => {
    if (e.defaultPrevented) return
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    e.preventDefault()
    zoomNavigate(navigate, to)
  }
}
